/* eslint-disable react/prop-types */
import {
  Add,
  ChatBubbleOutline,
  Delete,
  Edit,
  FilterList,
  VisibilityOutlined,
} from '@mui/icons-material'
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

import { PatientList } from '../../../providers/Patients/types'
import { Provider } from '../../../providers/ProvidersProvider/types'
import {
  AddCommentRequest,
  CreateProviderTaskRequest,
  EditCommentRequest,
  ProviderTaskBarrier,
  ProviderTaskHistory,
  ProviderTaskPriorityLevel,
  ProviderTaskResponse,
  ProviderTaskStatus,
  ProviderTaskType,
  UpdateProviderTaskRequest,
} from '../../../providers/ProviderTask/ProviderTask.provider'
import { themeColors } from '../../../theme'
import { CreateProviderTaskModal } from './CreateProviderTaskModal'
import { CreateProviderTaskTypeModal } from './CreateProviderTaskTypeModal'
import { DeleteProviderTaskModal } from './DeleteProviderTaskModal'
import { EditProviderTaskModal } from './EditProviderTaskModal'
import { TaskHistoryTimeline } from './ProviderTaskHistoryTimeline'
import { TaskComment } from './TaskComment'

interface ProviderTasksViewProps {
  isAdmin: boolean
  tasks: ProviderTaskResponse[]
  setOptimisticTasks: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse[]>
  > // for local state
  providers: Provider[]
  allActivePatients: PatientList
  myProviderId: number
  taskTypes: ProviderTaskType[]
  setTaskTypes: React.Dispatch<React.SetStateAction<ProviderTaskType[]>>
  priorityLevels: ProviderTaskPriorityLevel[]
  statuses: ProviderTaskStatus[]
  barriers: ProviderTaskBarrier[]
  getAllProviderTasks: () => Promise<any>
  getProviderTasksByProviderId: (providerId: number) => Promise<any>
  onCreateTask: (request: CreateProviderTaskRequest) => Promise<any>
  onUpdateTask: (request: UpdateProviderTaskRequest) => Promise<any>
  onDeleteTask: (providerTaskId: number) => Promise<any>
  onCreateTaskType: (type: string) => Promise<any>
  onAddProviderTaskComment: (request: AddCommentRequest) => Promise<any>
  onEditProviderTaskComment: (request: EditCommentRequest) => Promise<any>
  onDeleteProviderTaskComment: (providerTaskCommentId: number) => Promise<any>
  providerTaskHistory: ProviderTaskHistory[] | null
  onFetchProviderTaskHistory: (providerTaskId: number) => Promise<any>
}

const priorityColorMap: Record<string, string> = {
  LOW: themeColors.green,
  MEDIUM: themeColors.teal50,
  HIGH: themeColors.yellow,
  IMMEDIATE: themeColors.red,
}

const statusColorMap: Record<string, string> = {
  'NOT STARTED': themeColors.gray20,
  'IN PROGRESS': themeColors.blue,
  BARRIERS: themeColors.orange,
  COMPLETED: themeColors.green,
}

const tableCellHeaderStyles = {
  color: themeColors.white,
  fontWeight: 600,
  fontSize: 18,
}

const tableCellStyles = {
  fontWeight: 600,
  fontSize: 16,
}

export const ProviderTasksView = ({
  isAdmin,
  tasks,
  setOptimisticTasks,
  providers,
  allActivePatients,
  myProviderId,
  taskTypes,
  setTaskTypes,
  priorityLevels,
  statuses,
  barriers,
  getAllProviderTasks,
  getProviderTasksByProviderId,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onCreateTaskType,
  onAddProviderTaskComment,
  onEditProviderTaskComment,
  onDeleteProviderTaskComment,
  providerTaskHistory,
  onFetchProviderTaskHistory,
}: ProviderTasksViewProps) => {
  const [activeTab, setActiveTab] = useState<
    'my' | 'all' | 'myCompleted' | 'allCompleted'
  >('my')
  const [filterProviderId, setFilterProviderId] = useState<number>(0)
  const [filterPatientId, setFilterPatientId] = useState(0)
  const [filterTaskTypeId, setFilterTaskTypeId] = useState<number>(0)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCreateTypeModal, setShowCreateTypeModal] = useState(false)
  const [selectedTask, setSelectedTask] = useState<ProviderTaskResponse | null>(
    null
  )
  const [commentTask, setCommentTask] = useState<ProviderTaskResponse | null>(
    null
  )
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)

  // Create/Edit form state
  const [formPatientId, setFormPatientId] = useState<number>(0)
  const [formTaskTypeId, setFormTaskTypeId] = useState<number>(0)
  const [formAssignedToProviderId, setFormAssignedToProviderId] =
    useState<number>(0)
  const [formPriorityLevelId, setFormPriorityLevelId] = useState<number>(0)
  const [formStatusId, setFormStatusId] = useState<number>(0)
  const [formBarrierId, setFormBarrierId] = useState<number | null>(null)
  const [formAssignedAt, setFormAssignedAt] = useState<Dayjs | null>(dayjs())
  const [formDueAt, setFormDueAt] = useState<Dayjs | null>(null)

  // task history for timeline view
  const [historyTaskId, setHistoryTaskId] = useState<number | null>(null)

  const fetchTaskData = async () => {
    if (activeTab === 'my' || activeTab === 'myCompleted') {
      await getProviderTasksByProviderId(myProviderId)
    } else {
      await getAllProviderTasks()
    }
  }

  useEffect(() => {
    fetchTaskData()
  }, [activeTab])

  const resetForm = () => {
    setFormPatientId(0)
    setFormTaskTypeId(0)
    setFormAssignedToProviderId(0)
    setFormPriorityLevelId(0)
    setFormStatusId(0)
    setFormBarrierId(null)
    setFormAssignedAt(dayjs())
    setFormDueAt(null)
  }

  const handleOpenCreateModal = () => {
    resetForm()
    setShowCreateModal(true)
  }

  const handleOpenEditModal = (task: ProviderTaskResponse) => {
    setSelectedTask(task)
    setFormTaskTypeId(task.providerTaskType.id ?? 0)
    setFormAssignedToProviderId(task.assignedToProviderId)
    setFormPriorityLevelId(task.priorityLevel.id ?? 0)
    setFormStatusId(task.status.id)
    setFormBarrierId(task.barrier ? task.barrier.id ?? null : null)
    setFormDueAt(dayjs(task.dueAt))
    setShowEditModal(true)
  }

  const handleStatusOrBarrierChange = async (
    task: ProviderTaskResponse,
    newStatusId?: number,
    newBarrierId?: number | null
  ) => {
    // Optimistically update UI immediately
    setOptimisticTasks((prev) =>
      prev.map((t) => {
        if (t.id !== task.id) return t
        return {
          ...t,
          status: newStatusId
            ? statuses.find((s) => s.id === newStatusId) ?? t.status
            : t.status,
          barrier:
            newBarrierId !== undefined
              ? newBarrierId !== null
                ? barriers.find((b) => b.id === newBarrierId) ?? null
                : null
              : t.barrier,
        }
      })
    )

    // Sync with backend in background
    try {
      await onUpdateTask({
        providerTaskId: task.id,
        providerTaskTypeId: task.providerTaskType.id,
        assignedToProviderId: task.assignedToProviderId,
        providerTaskPriorityLevelId: task.priorityLevel.id,
        providerTaskStatusId: newStatusId ?? task.status.id,
        providerTaskBarrierId:
          newBarrierId !== undefined ? newBarrierId : task.barrier?.id ?? null,
        dueAt: task.dueAt,
      })
    } catch {
      // Rollback on failure
      setOptimisticTasks((prev) =>
        prev.map((t) => (t.id === task.id ? task : t))
      )
    }
  }

  const isCompletedTab =
    activeTab === 'myCompleted' || activeTab === 'allCompleted'

  // Filter tasks after tab change
  const filteredTasks = tasks.filter((task) => {
    if (isCompletedTab) {
      return task.status.status === 'COMPLETED'
    }

    // Active tabs: hide completed tasks
    if (task.status.status === 'COMPLETED') return false

    if (filterProviderId && task.assignedToProviderId !== filterProviderId)
      return false
    if (filterPatientId && task.patientId !== filterPatientId) return false
    if (filterTaskTypeId && task.providerTaskType.id !== filterTaskTypeId)
      return false
    return true
  })

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant='h4'
              fontWeight={600}
              onClick={() => setActiveTab('my')}
              sx={{
                cursor: 'pointer',
                color: activeTab === 'my' ? themeColors.grayblack : '#9e9e9e',
                borderBottom: activeTab === 'my' ? '2px solid' : 'none',
                pb: 0.5,
              }}
            >
              My Tasks
            </Typography>

            <Typography
              variant='h4'
              fontWeight={600}
              onClick={() => setActiveTab('myCompleted')}
              sx={{
                cursor: 'pointer',
                color:
                  activeTab === 'myCompleted'
                    ? themeColors.grayblack
                    : '#9e9e9e',
                borderBottom:
                  activeTab === 'myCompleted' ? '2px solid' : 'none',
                pb: 0.5,
              }}
            >
              My Completed
            </Typography>

            {isAdmin && (
              <>
                <Typography
                  variant='h4'
                  fontWeight={600}
                  onClick={() => setActiveTab('all')}
                  sx={{
                    cursor: 'pointer',
                    color:
                      activeTab === 'all' ? themeColors.grayblack : '#9e9e9e',
                    borderBottom: activeTab === 'all' ? '2px solid' : 'none',
                    pb: 0.5,
                  }}
                >
                  All Provider Tasks
                </Typography>

                <Typography
                  variant='h4'
                  fontWeight={600}
                  onClick={() => setActiveTab('allCompleted')}
                  sx={{
                    cursor: 'pointer',
                    color:
                      activeTab === 'allCompleted'
                        ? themeColors.grayblack
                        : '#9e9e9e',
                    borderBottom:
                      activeTab === 'allCompleted' ? '2px solid' : 'none',
                    pb: 0.5,
                  }}
                >
                  All Completed
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {isAdmin && activeTab === 'all' && (
              <>
                <Button
                  variant='contained'
                  startIcon={
                    <Add
                      sx={{ width: 24, height: 24, color: themeColors.white }}
                    />
                  }
                  sx={{
                    fontSize: 16,
                    backgroundColor: themeColors.blue,
                    height: 35,
                  }}
                  onClick={() => setShowCreateTypeModal(true)}
                >
                  <Typography color={themeColors.white}>
                    New Task Type
                  </Typography>
                </Button>

                <Button
                  variant='contained'
                  startIcon={
                    <Add
                      sx={{ width: 24, height: 24, color: themeColors.white }}
                    />
                  }
                  sx={{
                    fontSize: 16,
                    backgroundColor: themeColors.blue,
                    height: 35,
                  }}
                  onClick={handleOpenCreateModal}
                >
                  <Typography color={themeColors.white}>New Task</Typography>
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Filters (Admin only) */}
        {isAdmin && activeTab === 'all' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <FilterList sx={{ color: themeColors.darkPurple }} />
            <Autocomplete
              size='small'
              sx={{ minWidth: 200 }}
              options={providers}
              getOptionLabel={(option) => option.name}
              value={
                providers.find((p) => p.providerId === filterProviderId) || null
              }
              onChange={(_, newValue) =>
                setFilterProviderId(newValue ? newValue.providerId : 0)
              }
              renderInput={(params) => (
                <TextField {...params} label='Filter by Provider' />
              )}
              isOptionEqualToValue={(option, value) =>
                option.providerId === value.providerId
              }
            />

            <Autocomplete
              size='small'
              sx={{ minWidth: 200 }}
              options={allActivePatients}
              getOptionLabel={(option) => option.preferredName}
              value={
                allActivePatients.find(
                  (p) => p.patientId === filterPatientId
                ) || null
              }
              onChange={(_, newValue) =>
                setFilterPatientId(newValue ? newValue.patientId : 0)
              }
              renderInput={(params) => (
                <TextField {...params} label='Search by Patient' />
              )}
            />

            <Autocomplete
              size='small'
              sx={{ minWidth: 200 }}
              options={taskTypes}
              getOptionLabel={(option) => option.type}
              value={taskTypes.find((t) => t.id === filterTaskTypeId) || null}
              onChange={(_, newValue) =>
                setFilterTaskTypeId(newValue ? newValue.id : 0)
              }
              renderInput={(params) => (
                <TextField {...params} label='Filter by Task Type' />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>
        )}

        {/* Task Table */}
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: themeColors.darkPurple }}>
                <TableCell sx={tableCellHeaderStyles}>Task ID</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Patient</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Task Type</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Assigned To</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Priority</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Status</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Barrier</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Due Date</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Created</TableCell>
                <TableCell sx={tableCellHeaderStyles}>Comment</TableCell>
                {isAdmin && (
                  <TableCell sx={tableCellHeaderStyles}>Actions</TableCell>
                )}
                <TableCell sx={tableCellHeaderStyles}>History</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={isAdmin ? 8 : 6}
                    align='center'
                    sx={{ py: 4 }}
                  >
                    <Typography color='textSecondary'>
                      No tasks found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task: ProviderTaskResponse) => (
                  <TableRow key={task.id} hover>
                    <TableCell sx={tableCellStyles}>{task.id}</TableCell>

                    <TableCell sx={tableCellStyles}>
                      {task.patientName}
                    </TableCell>

                    <TableCell sx={tableCellStyles}>
                      {task.providerTaskType.type}
                    </TableCell>

                    <TableCell sx={tableCellStyles}>
                      {task.assignedToProvider}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={task.priorityLevel.level}
                        size='small'
                        sx={{
                          backgroundColor:
                            priorityColorMap[task.priorityLevel.level] ??
                            themeColors.grayWhite,
                          '& .MuiChip-label': {
                            color: themeColors.white,
                          },
                          fontWeight: 600,
                          border: 'none',
                        }}
                      />
                    </TableCell>

                    {/* Status Dropdown */}
                    <TableCell sx={{ padding: 0 }}>
                      <Select
                        value={task.status.id}
                        size='small'
                        onChange={(e) =>
                          handleStatusOrBarrierChange(
                            task,
                            Number(e.target.value),
                            undefined // not changing barrier
                          )
                        }
                        renderValue={(value) => {
                          const selected = statuses.find((s) => s.id === value)
                          return (
                            <Chip
                              label={selected?.status ?? 'Unknown'}
                              size='small'
                              sx={{
                                backgroundColor: selected?.status
                                  ? statusColorMap[selected?.status]
                                  : themeColors.grayWhite,
                                '& .MuiChip-label': {
                                  color: themeColors.white,
                                },
                                fontWeight: 600,
                                border: 'none',
                              }}
                            />
                          )
                        }}
                        sx={{
                          '.MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '.MuiSelect-select': { padding: '2px 8px' },
                        }}
                      >
                        {statuses.map((status) => (
                          <MenuItem key={status.status} value={status.id}>
                            <Chip
                              label={status.status}
                              size='small'
                              sx={{
                                '& .MuiChip-label': {
                                  color: themeColors.white,
                                },
                                backgroundColor:
                                  statusColorMap[status.status] ??
                                  themeColors.grayWhite,
                                fontWeight: 600,
                                border: 'none',
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* Barrier Dropdown */}
                    <TableCell sx={{ padding: 0 }}>
                      <Select
                        value={task.barrier?.id}
                        size='small'
                        displayEmpty
                        onChange={(e) =>
                          handleStatusOrBarrierChange(
                            task,
                            undefined, // not changing status
                            e.target.value === ''
                              ? null
                              : Number(e.target.value)
                          )
                        }
                        renderValue={(value) => {
                          const selected = barriers.find((b) => b.id === value)
                          return selected ? (
                            <Chip
                              label={selected.barrier}
                              size='small'
                              sx={{
                                backgroundColor: themeColors.red,
                                '& .MuiChip-label': {
                                  color: themeColors.white,
                                },
                                fontWeight: 600,
                                border: 'none',
                              }}
                            />
                          ) : (
                            <Chip
                              label='NO BARRIER'
                              size='small'
                              sx={{
                                backgroundColor: themeColors.gray20,
                                '& .MuiChip-label': {
                                  color: themeColors.white,
                                },
                                fontWeight: 600,
                                border: 'none',
                              }}
                            />
                          )
                        }}
                        sx={{
                          '.MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '.MuiSelect-select': { padding: '2px 8px' },
                        }}
                      >
                        <MenuItem value=''>
                          <Chip
                            label='NO BARRIER'
                            size='small'
                            sx={{
                              backgroundColor: themeColors.gray20,
                              '& .MuiChip-label': { color: themeColors.white },
                              fontWeight: 600,
                              border: 'none',
                            }}
                          />
                        </MenuItem>

                        {barriers.map((barrier) => (
                          <MenuItem key={barrier.barrier} value={barrier.id}>
                            <Chip
                              label={barrier.barrier}
                              size='small'
                              sx={{
                                '& .MuiChip-label': {
                                  color: themeColors.white,
                                  border: 'none',
                                },
                                backgroundColor:
                                  themeColors.red ?? themeColors.grayWhite,
                                fontWeight: 600,
                                border: 'none',
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    <TableCell sx={tableCellStyles}>
                      {dayjs(task.dueAt).format('MM/DD/YYYY')}
                    </TableCell>

                    <TableCell sx={tableCellStyles}>
                      {dayjs(task.createdAt).format('MM/DD/YYYY')}
                    </TableCell>

                    <TableCell>
                      <Tooltip title='Comments'>
                        <IconButton
                          size='medium'
                          onClick={() => setCommentTask(task)}
                        >
                          <Badge
                            badgeContent={
                              task.providerTaskComments?.length ?? 0
                            }
                            color='primary'
                          >
                            <ChatBubbleOutline fontSize='medium' />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {isAdmin && (
                      <TableCell>
                        <Tooltip title='Edit'>
                          <IconButton
                            size='medium'
                            onClick={() => handleOpenEditModal(task)}
                          >
                            <Edit fontSize='medium' />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title='Delete'>
                          <IconButton
                            size='medium'
                            color='error'
                            onClick={() => {
                              setSelectedTask(task)
                              setShowDeleteConfirm(true)
                            }}
                          >
                            <Delete fontSize='medium' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}

                    <TableCell sx={tableCellStyles}>
                      <Tooltip title='View History'>
                        <IconButton
                          size='medium'
                          onClick={() => setHistoryTaskId(task.id)}
                        >
                          <VisibilityOutlined fontSize='medium' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CreateProviderTaskTypeModal
          loading={loading}
          setLoading={setLoading}
          showCreateTypeModal={showCreateTypeModal}
          setShowCreateTypeModal={setShowCreateTypeModal}
          onCreateTaskType={onCreateTaskType}
          setTaskTypes={setTaskTypes}
        />
        <CreateProviderTaskModal
          loading={loading}
          setLoading={setLoading}
          showCreateModal={showCreateModal}
          setShowCreateModal={setShowCreateModal}
          allActivePatients={allActivePatients}
          taskTypes={taskTypes}
          providers={providers}
          priorityLevels={priorityLevels}
          statuses={statuses}
          barriers={barriers}
          formTaskTypeId={formTaskTypeId}
          setFormTaskTypeId={setFormTaskTypeId}
          formAssignedToProviderId={formAssignedToProviderId}
          setFormAssignedToProviderId={setFormAssignedToProviderId}
          formPriorityLevelId={formPriorityLevelId}
          setFormPriorityLevelId={setFormPriorityLevelId}
          formStatusId={formStatusId}
          setFormStatusId={setFormStatusId}
          formBarrierId={formBarrierId}
          setFormBarrierId={setFormBarrierId}
          formDueAt={formDueAt}
          setFormDueAt={setFormDueAt}
          formAssignedAt={formAssignedAt}
          formPatientId={formPatientId}
          setFormPatientId={setFormPatientId}
          setOptimisticTasks={setOptimisticTasks}
          onCreateTask={onCreateTask}
        />
        <EditProviderTaskModal
          loading={loading}
          setLoading={setLoading}
          showEditModal={showEditModal}
          setShowEditModal={setShowEditModal}
          selectedTask={selectedTask}
          taskTypes={taskTypes}
          providers={providers}
          priorityLevels={priorityLevels}
          statuses={statuses}
          barriers={barriers}
          formTaskTypeId={formTaskTypeId}
          setFormTaskTypeId={setFormTaskTypeId}
          formAssignedToProviderId={formAssignedToProviderId}
          setFormAssignedToProviderId={setFormAssignedToProviderId}
          formPriorityLevelId={formPriorityLevelId}
          setFormPriorityLevelId={setFormPriorityLevelId}
          formStatusId={formStatusId}
          setFormStatusId={setFormStatusId}
          formBarrierId={formBarrierId}
          setFormBarrierId={setFormBarrierId}
          formDueAt={formDueAt}
          setFormDueAt={setFormDueAt}
          onUpdateTask={onUpdateTask}
          setOptimisticTasks={setOptimisticTasks}
        />
        <TaskComment
          loading={loading}
          setLoading={setLoading}
          myProviderId={myProviderId}
          commentTask={commentTask}
          setCommentTask={setCommentTask}
          setNewComment={setNewComment}
          newComment={newComment}
          setOptimisticTasks={setOptimisticTasks}
          onAddProviderTaskComment={onAddProviderTaskComment}
          onEditProviderTaskComment={onEditProviderTaskComment}
          onDeleteProviderTaskComment={onDeleteProviderTaskComment}
        />
        <DeleteProviderTaskModal
          loading={loading}
          setLoading={setLoading}
          tasks={tasks}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
          showDeleteConfirm={showDeleteConfirm}
          setShowDeleteConfirm={setShowDeleteConfirm}
          onDeleteTask={onDeleteTask}
          setOptimisticTasks={setOptimisticTasks}
        />
      </Box>
      <TaskHistoryTimeline
        open={historyTaskId !== null}
        onClose={() => setHistoryTaskId(null)}
        taskId={historyTaskId}
        providerTaskHistory={providerTaskHistory}
        onFetchProviderTaskHistory={onFetchProviderTaskHistory}
      />
    </LocalizationProvider>
  )
}
